/**
 * Per-device preferences (sidebar collapsed, which nav groups are closed, theme) kept in
 * localStorage. localStorage THROWS in a private window, some embedded webviews and when site
 * data is blocked, so every access is guarded: a failure only means the choice is not remembered.
 */
import { useCallback, useState } from 'react';

interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function store(): KeyValueStore | undefined {
  try {
    return (globalThis as { localStorage?: KeyValueStore }).localStorage;
  } catch {
    return undefined;
  }
}

export function readDeviceSetting(key: string): string | null {
  try {
    return store()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

export function writeDeviceSetting(key: string, value: string | null): void {
  try {
    const s = store();
    if (value === null) s?.removeItem(key);
    else s?.setItem(key, value);
  } catch {
    // Not remembered; the in-memory value still applies.
  }
}

/** A boolean remembered on this device. */
export function useDeviceFlag(key: string, initial: boolean): [boolean, (next: boolean) => void] {
  const [value, setValue] = useState<boolean>(() => {
    const raw = readDeviceSetting(key);
    return raw === null ? initial : raw === '1';
  });
  const set = useCallback((next: boolean) => {
    setValue(next);
    writeDeviceSetting(key, next ? '1' : '0');
  }, [key]);
  return [value, set];
}

/** A set of string keys remembered on this device (e.g. the nav groups a person closed). */
export function useDeviceSet(key: string): [ReadonlySet<string>, (item: string, member: boolean) => void] {
  const [value, setValue] = useState<ReadonlySet<string>>(() => {
    const raw = readDeviceSetting(key);
    if (!raw) return new Set<string>();
    try {
      const parsed: unknown = JSON.parse(raw);
      return new Set(Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : []);
    } catch {
      return new Set<string>();
    }
  });
  const set = useCallback((item: string, member: boolean) => {
    setValue((prev) => {
      const next = new Set(prev);
      if (member) next.add(item);
      else next.delete(item);
      writeDeviceSetting(key, JSON.stringify([...next].sort()));
      return next;
    });
  }, [key]);
  return [value, set];
}
